import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Target, Users, Zap, Lightbulb, Home, ChevronDown, ChevronUp, Download, AlertTriangle, TrendingUp, Award, Rocket, Star } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { createPDF, brandedAutoTable, addFooter, addHeader } from "@/utils/pdfUtils";
import {
  DIMENSION_BANDS, INTERVENTION_BANDS, RESET_INTERVENTION,
  getBand, getInterventionBand, getPriorityLever as getPriorityLeverUtil,
  checkResetCondition, getDevelopmentFocus as getDevelopmentFocusUtil, getDimensionLevelKey
} from "@/utils/consultingLogic";

export default function ResultsPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [results, setResults] = useState(null);
  const [event, setEvent] = useState(null);
  const [showRecommendation, setShowRecommendation] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [isOpenMode, setIsOpenMode] = useState(false);

  useEffect(() => {
    const storedResults = sessionStorage.getItem("teampower_results");
    const storedEvent = sessionStorage.getItem("teampower_event");
    setIsOpenMode(sessionStorage.getItem("teampower_open_mode") === "true");
    if (storedResults) {
      setResults(JSON.parse(storedResults));
    } else {
      navigate("/");
    }
    if (storedEvent) {
      setEvent(JSON.parse(storedEvent));
    }
  }, [navigate]);

  // Get the band for a given score in a dimension - delegate to shared utility
  const getLocalBand = (dimension, score) => getBand(dimension, score);

  // Get the intervention intensity band for overall score
  const getLocalInterventionBand = (score) => getInterventionBand(score);

  // Get priority lever (lowest scoring dimension)
  const getPriorityLever = () => {
    if (!results) return null;
    return getPriorityLeverUtil(results.capability_score, results.collaboration_score, results.energy_score);
  };

  // Check if two or more dimensions are below 45 (RESET condition)
  const localCheckResetCondition = () => {
    if (!results) return false;
    return checkResetCondition(results.capability_score, results.collaboration_score, results.energy_score);
  };

  // Get development focus based on priority lever logic
  const getDevelopmentFocus = () => {
    if (!results) return null;
    return getDevelopmentFocusUtil(results.capability_score, results.collaboration_score, results.energy_score);
  };

  // Translate outcome/feature key
  const translateOutcome = (key) => {
    return t(`outcomes.${key}`) || key;
  };

  const translateTraining = (key) => {
    return t(`training.${key}`) || key;
  };

  // For uppercase display (font-heading class)
  const getTranslatedLevelUpper = (level) => {
    if (!level) return '';
    const levelKey = level.toLowerCase().replace(/\s+/g, '');
    if (levelKey.includes('top') || levelKey.includes('advanced') || levelKey.includes('high')) return t('levels.topLevelUpper');
    if (levelKey.includes('good') || levelKey.includes('functional')) return t('levels.goodLevelUpper');
    if (levelKey.includes('base') || levelKey.includes('developing')) return t('levels.baseLevelUpper');
    return t('levels.developmentNeededUpper');
  };

  // For normal case display
  const getTranslatedLevel = (level) => {
    if (!level) return '';
    const levelKey = level.toLowerCase().replace(/\s+/g, '');
    if (levelKey.includes('top') || levelKey.includes('advanced') || levelKey.includes('high')) return t('levels.topLevel');
    if (levelKey.includes('good') || levelKey.includes('functional')) return t('levels.goodLevel');
    if (levelKey.includes('base') || levelKey.includes('developing')) return t('levels.baseLevel');
    return t('levels.developmentNeeded');
  };

  // Get dimension level from new bands
  const getDimensionLevel = (dimension, score) => {
    const key = getDimensionLevelKey(dimension, score);
    return t(`dimensionLevels.${key}`) || getBand(dimension, score).level;
  };

  const downloadPDF = async () => {
    if (!results) return;
    setDownloading(true);

    try {
      const doc = createPDF();
      const interventionBand = getInterventionBand(results.teampower_index);
      const developmentFocus = getDevelopmentFocus();
      
      // Header with TeamPower logo
      const contentY = addHeader(doc);
      
      // Event info
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      if (event) {
        doc.text(event.name, 105, contentY + 2, { align: "center" });
      }
      
      // Overall Score
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text(t('results.yourIndex'), 20, contentY + 20);
      
      doc.setFontSize(36);
      doc.setTextColor(236, 72, 153);
      doc.text(`${results.teampower_index}`, 20, contentY + 37);
      
      // Intervention Intensity
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`${t('results.interventionIntensity')}: ${t(`intervention.${interventionBand.intensityKey}`)}`, 20, contentY + 47);

      // Dimension Scores Table
      const dimensionData = [
        [t('dimensions.capability'), `${results.capability_score.toFixed(1)}%`, getDimensionLevel('capability', results.capability_score)],
        [t('dimensions.collaboration'), `${results.collaboration_score.toFixed(1)}%`, getDimensionLevel('collaboration', results.collaboration_score)],
        [t('dimensions.energy'), `${results.energy_score.toFixed(1)}%`, getDimensionLevel('energy', results.energy_score)]
      ];

      brandedAutoTable(doc, {
        startY: contentY + 55,
        head: [[t('pdf.dimension') || "Dimension", t('pdf.score') || "Score", t('pdf.level') || "Level"]],
        body: dimensionData,
        theme: "striped",
        styles: { fontSize: 10 },
      });

      // Priority Lever
      const pdfPriorityLever = getPriorityLever();
      const priorityY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(t('results.priorityFocus'), 20, priorityY);
      doc.setFontSize(12);
      doc.setTextColor(236, 72, 153);
      doc.text(t(`dimensions.${pdfPriorityLever[0].dimension}`), 20, priorityY + 10);

      // Development Focus
      const devY = priorityY + 25;
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(t('results.developmentFocus'), 20, devY);
      
      if (developmentFocus.isReset) {
        doc.setFontSize(10);
        doc.setTextColor(180, 0, 0);
        doc.text(t('results.resetAlertTitle'), 20, devY + 10);
      }

      // Teambuilding Outcomes
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(t('results.teambuildingOutcomes') + ":", 20, devY + 22);
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      developmentFocus.teambuildingOutcomes.forEach((outcome, i) => {
        const txt = `• ${translateOutcome(outcome)}`;
        doc.text(txt, 25, devY + 32 + (i * 7));
      });

      // Training Features
      const trainY = devY + 32 + (developmentFocus.teambuildingOutcomes.length * 7) + 10;
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(t('results.trainingFeatures') + ":", 20, trainY);
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      developmentFocus.trainingFeatures.forEach((feature, i) => {
        const txt = `• ${translateTraining(feature)}`;
        doc.text(txt, 25, trainY + 10 + (i * 7));
      });

      // Footer
      addFooter(doc);

      doc.save(`TeamPower_Results_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error("PDF generation error:", error);
    } finally {
      setDownloading(false);
    }
  };

  if (!results) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const interventionBand = getInterventionBand(results.teampower_index);
  const developmentFocus = getDevelopmentFocus();
  const priorityLever = getPriorityLever();

  // Map intervention band color to icon (icons not in shared utility)
  const interventionIconMap = { red: AlertTriangle, orange: TrendingUp, yellow: Rocket, green: Award, blue: Star };
  const InterventionIcon = interventionIconMap[interventionBand.color] || AlertTriangle;

  const dimensions = [
    {
      key: "capability",
      label: t('dimensions.capabilityUpper'),
      icon: Target,
      color: "blue",
      avg: results.capability_avg,
      score: results.capability_score,
      level: getDimensionLevel('capability', results.capability_score),
    },
    {
      key: "collaboration",
      label: t('dimensions.collaborationUpper'),
      icon: Users,
      color: "purple",
      avg: results.collaboration_avg,
      score: results.collaboration_score,
      level: getDimensionLevel('collaboration', results.collaboration_score),
    },
    {
      key: "energy",
      label: t('dimensions.energyUpper'),
      icon: Zap,
      color: "orange",
      avg: results.energy_avg,
      score: results.energy_score,
      level: getDimensionLevel('energy', results.energy_score),
    },
  ];

  const colorClasses = {
    blue: {
      bg: "bg-blue-100",
      text: "text-blue-600",
      border: "border-blue-200",
      progress: "bg-blue-500",
      levelBg: "bg-blue-50",
    },
    purple: {
      bg: "bg-purple-100",
      text: "text-purple-600",
      border: "border-purple-200",
      progress: "bg-purple-500",
      levelBg: "bg-purple-50",
    },
    orange: {
      bg: "bg-pink-100",
      text: "text-pink-600",
      border: "border-pink-200",
      progress: "bg-pink-500",
      levelBg: "bg-pink-50",
    },
  };

  const interventionColors = {
    red: "bg-red-100 text-red-700 border-red-200",
    orange: "bg-orange-100 text-orange-700 border-orange-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
    green: "bg-green-100 text-green-700 border-green-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-pink-600 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading text-sm tracking-wide text-gray-900">TEAMPOWER CHECK</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="text-gray-600 hover:text-gray-900 border-gray-300"
              data-testid="home-button"
            >
              <Home className="w-4 h-4 mr-2" />
              {t('results.newAssessment')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* TeamPower Index & Intervention Intensity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 text-center mb-8 shadow-sm"
          data-testid="teampower-index-card"
        >
          <p className="text-gray-500 mb-4">{t('results.yourIndex')}</p>
          <div className="relative inline-block mb-4">
            <motion.p
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-pink-700"
              data-testid="teampower-score"
            >
              {results.teampower_index}
            </motion.p>
          </div>
          
          {/* Intervention Intensity Badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${interventionColors[interventionBand.color]} mb-4`}>
            <InterventionIcon className="w-5 h-5" />
            <span className="font-medium text-sm">{t(`intervention.${interventionBand.intensityKey}`)}</span>
          </div>
          
          <p className="text-gray-600 text-sm max-w-2xl mx-auto mt-4">
            {t(`intervention.${interventionBand.intensityKey}Desc`)}
          </p>
        </motion.div>

        {/* Priority Lever Alert (if RESET condition) */}
        {developmentFocus.isReset && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-red-800 mb-2">{t('results.resetAlertTitleUpper')}</h3>
                <p className="text-red-700 text-sm">{t('results.resetAlertDesc')}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Dimension Scores */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {dimensions.map((dim, index) => {
            const colors = colorClasses[dim.color];
            const isPriority = priorityLever[0].dimension === dim.key;
            return (
              <motion.div
                key={dim.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                className={`bg-white border ${isPriority ? 'border-pink-400 ring-2 ring-pink-100' : colors.border} rounded-xl p-6 shadow-sm relative`}
                data-testid={`dimension-card-${dim.key}`}
              >
                {isPriority && (
                  <div className="absolute -top-3 left-4 bg-pink-600 text-white text-xs px-2 py-1 rounded-full">
                    {t('results.priorityFocus')}
                  </div>
                )}
                
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                    <dim.icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <span className={`font-heading text-lg ${colors.text}`}>{dim.label}</span>
                </div>

                {/* Average Score */}
                <p className="text-gray-500 text-sm mb-1">{t('results.averageScore')}</p>
                <p className={`text-3xl font-bold ${colors.text} mb-4`}>
                  {dim.avg.toFixed(2)} <span className="text-gray-400 text-lg">/ 5</span>
                </p>

                {/* Progress Bar */}
                <p className="text-gray-500 text-sm mb-2">{t('results.score')}</p>
                <div className="relative mb-3">
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${dim.score}%` }}
                      transition={{ delay: 0.3 + index * 0.1, duration: 0.8 }}
                      className={`h-full ${colors.progress} rounded-full`}
                    />
                  </div>
                  <span className="absolute right-0 -top-5 text-sm font-medium text-gray-600">
                    {dim.score.toFixed(1)}%
                  </span>
                </div>

                {/* Level Badge */}
                <div className={`${colors.levelBg} rounded-lg px-3 py-2 mt-4`}>
                  <span className="text-sm text-gray-600">{dim.level}</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Development Focus Recommendation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8 shadow-sm"
          data-testid="recommendation-card"
        >
          <button
            onClick={() => setShowRecommendation(!showRecommendation)}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-pink-600" />
              </div>
              <span className="font-heading text-lg text-gray-900">{t('results.developmentFocus')}</span>
            </div>
            {showRecommendation ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showRecommendation && (
            <div className="px-6 pb-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Teambuilding Outcomes */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-pink-600" />
                    {t('results.teambuildingOutcomes')}
                  </h4>
                  <ul className="space-y-3">
                    {developmentFocus.teambuildingOutcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-gray-700">{translateOutcome(outcome)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Training Features */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-pink-600" />
                    {t('results.trainingFeatures')}
                  </h4>
                  <ul className="space-y-3">
                    {developmentFocus.trainingFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-gray-700">{translateTraining(feature)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Download & Footer */}
        <div className="text-center">
          <p className="text-gray-500 text-sm mb-4">
            {t('results.thankYou')}
          </p>
          
          {/* Download Button */}
          <Button
            onClick={downloadPDF}
            disabled={downloading}
            className="mb-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium px-6"
            data-testid="download-results-button"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? t('results.downloadingPdf') : t('results.downloadResults')}
          </Button>
          
          <div>
            <Button
              onClick={() => navigate("/")}
              className="bg-pink-600 hover:bg-pink-700 text-white font-bold shadow-lg"
              data-testid="finish-button"
            >
              <Home className="w-4 h-4 mr-2" />
              {t('results.backToHome')}
            </Button>
          </div>
        </div>

        {/* Summit CTA for Open Mode */}
        {isOpenMode && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-10 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-2xl p-6 sm:p-8 text-center"
            data-testid="summit-cta"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {language === 'el' ? 'ΘΕΛΕΙΣ ΝΑ ΕΝΔΥΝΑΜΩΣΕΙΣ ΤΗΝ ΟΜΑΔΑ ΣΟΥ?' : 'Ready to empower your team?'}
            </h3>
            <p className="text-gray-600 mb-4 max-w-lg mx-auto">
              {language === 'el'
                ? 'Στη Summit προσφέρουμε εξατομικευμένες λύσεις teambuilding και εκπαίδευσης βασισμένες στα αποτελέσματά σου.'
                : 'Summit offers tailored teambuilding and training solutions based on your results.'}
            </p>
            <a
              href="https://www.summit.gr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white font-bold px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="contact-summit-link"
            >
              {language === 'el' ? 'Επικοινώνησε με τη Summit' : 'Contact Summit for tailored solutions'}
            </a>
          </motion.div>
        )}

        {/* Footer with Logo */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col items-center gap-4">
          <img 
            src="/summit-logo.png" 
            alt="Summit Logo" 
            className="h-10 object-contain"
          />
          <p className="text-gray-500 text-sm">
            {t('landing.poweredBy')}
          </p>
        </div>
      </main>
    </div>
  );
}
